// ── Avatar Creator Type Definitions ──────────────────────────────────────

/** Palette indices used in sprite grids: 0 = transparent, 1+ = color slot */
export type PaletteIndex = number;

/** A 2D grid of palette indices defining a pixel sprite */
export type SpriteGrid = PaletteIndex[][];

/** Color slots that map palette indices to actual hex colors */
export interface PaletteColors {
  bodyPrimary: string;
  bodySecondary: string;
  eyeColor: string;
  mouthColor: string;
  accessoryColor: string;
  backgroundColor: string;
}

/** Full avatar configuration (the saved state) */
export interface AvatarConfig {
  entityType: string;
  entityCategory: 'malware' | 'actor' | 'creature';
  eyes: string;
  mouth: string;
  accessory: string;
  background: string;
  palette: PaletteColors;
  gridSize: 14 | 16 | 24 | 32;
  name: string;
  /** User-added colors beyond the 6 palette slots */
  customColors?: string[];
  /** "row,col" → hex color — post-composition paint layer */
  pixelOverrides?: Record<string, string>;
}

/** Anchor points on a base entity where overlays attach */
export interface AnchorPoints {
  eyes: [number, number];   // [row, col]
  mouth: [number, number];
  accessory: [number, number];
}

/** A base entity sprite definition */
export interface SpriteDefinition {
  id: string;
  name: string;
  category: 'malware' | 'actor' | 'creature';
  grid: SpriteGrid;
  anchorPoints: AnchorPoints;
}

/** An overlay part (eyes, mouth, accessory) */
export interface PartDefinition {
  id: string;
  name: string;
  type: 'eyes' | 'mouth' | 'accessory';
  grid: SpriteGrid;
}

/** A background pattern definition */
export interface BackgroundDefinition {
  id: string;
  name: string;
  /** Pattern tile grid — repeated to fill the canvas */
  grid: SpriteGrid;
}

/** A preset palette with named colors */
export interface PalettePreset {
  id: string;
  name: string;
  colors: PaletteColors;
}

/** Step definition for the wizard */
export interface StepDef {
  key: string;
  label: string;
  description: string;
}

/** The 6 wizard steps */
export const WIZARD_STEPS: StepDef[] = [
  { key: 'base',       label: 'Base',       description: 'Choose your entity type' },
  { key: 'eyes',       label: 'Eyes',       description: 'Pick an eye style' },
  { key: 'mouth',      label: 'Mouth',      description: 'Give it some character' },
  { key: 'accessory',  label: 'Accessory',  description: 'Add some flair' },
  { key: 'background', label: 'Background', description: 'Set the scene' },
  { key: 'colors',     label: 'Colors',     description: 'Choose your palette' },
];

/** Default avatar config */
export function defaultAvatarConfig(): AvatarConfig {
  return {
    entityType: 'virus',
    entityCategory: 'malware',
    eyes: 'normal',
    mouth: 'smile',
    accessory: 'none',
    background: 'none',
    palette: {
      bodyPrimary: '#f38ba8',
      bodySecondary: '#eba0ac',
      eyeColor: '#89b4fa',
      mouthColor: '#a6e3a1',
      accessoryColor: '#cba6f7',
      backgroundColor: '#313244',
    },
    gridSize: 14,
    name: 'threat_avatar_01',
  };
}
